/**
 * @file     FairRentCheck.tsx
 * @purpose  "Fair rent check": where this rent sits against ASKING rents for
 *           similar homes nearby (public, server-computed from active IDX
 *           rentals, hidden under 8 comparables), drawn as a range bar with the
 *           home marked. Below it, the leased comparables: VOW data, fetched
 *           from /api/rentals/[key]/fair-rent only for a signed-in visitor and
 *           returned only to a VOW registrant; everyone else sees a GatedPanel
 *           of drawn rows (never values) that opens the VOW gate.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { GatedPanel } from '@/components/gate/GatedPanel';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { useAuth } from '@/hooks/useAuth';
import { useListingEvents } from '@/hooks/useListingEvents';
import { useVowUnlockToken } from '@/hooks/useVowUnlock';
import { useRentalsText } from '@/features/rentals-listing/useRentalsText';
import { formatRent } from '@/services/rentals/leaseListing';
import type { FairRentPosition } from '@/services/rentals/fairRent';
import type { RentBand, RentComparable } from '@/services/listing-submissions/rentRange';

interface Props {
  /** MLS® listing key, or the owner listing id: the fair-rent route accepts both. */
  subjectKey: string;
  rent: number;
  beds: number | null;
  place: string;
  asking: RentBand;
  position: FairRentPosition;
  analyticsSource: 'mls' | 'owner';
  /** Server-known sign-in state, so the gate paints right on first render. */
  isAuthenticated: boolean;
  /** A fair-rent response the caller already fetched (quick views): used once instead of a second request. */
  prefetched?: FairRentResponse | null;
}

export interface FairRentResponse {
  asking?: RentBand | null;
  position?: FairRentPosition | null;
  leasedLocked?: boolean;
  leased?: RentBand | null;
  comparables?: RentComparable[];
}

type VowState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'locked' }
  | { kind: 'open'; leased: RentBand | null; comparables: RentComparable[] }
  | { kind: 'error' };

/** Bar geometry, as percentages of the track. Pure, for the tests. */
export function rangeGeometry(rent: number, band: Pick<RentBand, 'low' | 'high'>) {
  const lo = Math.min(band.low, rent);
  const hi = Math.max(band.high, rent);
  const pad = Math.max((hi - lo) * 0.25, hi * 0.04, 1);
  const min = lo - pad;
  const span = hi + pad - min;
  const pct = (v: number) => Math.round(((v - min) / span) * 1000) / 10;
  return { bandLeft: pct(band.low), bandWidth: pct(band.high) - pct(band.low), marker: pct(rent) };
}

export function FairRentCheck({ subjectKey, rent, beds, place, asking, position, analyticsSource, isAuthenticated, prefetched = null }: Props) {
  const tr = useRentalsText();
  const track = useListingEvents();
  const auth = useAuth();
  const signedIn = isAuthenticated || auth.isAuthenticated;
  const unlockToken = useVowUnlockToken();
  const [vow, setVow] = useState<VowState>({ kind: signedIn ? 'loading' : 'locked' });
  const ref = useRef<HTMLDivElement>(null);
  const viewedRef = useRef(false);
  const kind = { listing_type: 'lease' as const, listing_source: analyticsSource };
  const prefetchedRef = useRef(prefetched);

  useEffect(() => {
    if (!signedIn) {
      setVow({ kind: 'locked' });
      return;
    }
    const first = prefetchedRef.current;
    if (first) {
      prefetchedRef.current = null;
      setVow(first.leasedLocked || !first.comparables ? { kind: 'locked' } : { kind: 'open', leased: first.leased ?? null, comparables: first.comparables });
      return;
    }
    const ctrl = new AbortController();
    setVow({ kind: 'loading' });
    fetch(`/api/rentals/${encodeURIComponent(subjectKey)}/fair-rent`, { signal: ctrl.signal, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body: { leasedLocked?: boolean; leased?: RentBand | null; comparables?: RentComparable[] }) => {
        if (ctrl.signal.aborted) return;
        if (body.leasedLocked || !body.comparables) setVow({ kind: 'locked' });
        else setVow({ kind: 'open', leased: body.leased ?? null, comparables: body.comparables });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setVow({ kind: 'error' });
      });
    return () => ctrl.abort();
  }, [signedIn, subjectKey, unlockToken]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (viewedRef.current || !entries.some((e) => e.isIntersecting)) return;
      viewedRef.current = true;
      track('fair_rent_viewed', { listing_key: subjectKey, band: position, vow: vow.kind === 'open', ...kind });
      io.disconnect();
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
    // Once per page view; the VOW state at that moment is what the visitor saw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectKey, position]);

  const geo = rangeGeometry(rent, asking);
  const bandLine =
    beds != null && beds > 0
      ? tr('fair.bandLine', { beds, place, low: formatRent(asking.low), high: formatRent(asking.high) })
      : tr('fair.bandLineStudio', { place, low: formatRent(asking.low), high: formatRent(asking.high) });

  return (
    <Box ref={ref} data-fair-rent={position}>
      <Box component="p" sx={{ m: 0, fontSize: 16, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
        {tr(`fair.${position}`)}
      </Box>
      <Box component="p" sx={{ m: 0, mt: 1, fontSize: 15, lineHeight: 1.5, color: hubColors.textSecondary }}>
        {bandLine}
      </Box>

      <Box role="img" aria-label={tr('fair.rangeLabel', { low: formatRent(asking.low), high: formatRent(asking.high), rent: formatRent(rent) })} sx={{ position: 'relative', mt: 7, mb: 2, height: 52 }}>
        <Box
          component="span"
          aria-hidden
          sx={{ position: 'absolute', top: 0, left: `${geo.marker}%`, transform: 'translateX(-50%)', fontSize: 12.5, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
        >
          {tr('fair.thisHome', { rent: formatRent(rent) })}
        </Box>
        <Box aria-hidden sx={{ position: 'absolute', top: 24, left: 0, right: 0, height: 6, borderRadius: `${layoutTokens.radius.pill}px`, backgroundColor: hubColors.surfaceSunken }} />
        <Box aria-hidden sx={{ position: 'absolute', top: 24, left: `${geo.bandLeft}%`, width: `${geo.bandWidth}%`, height: 6, borderRadius: `${layoutTokens.radius.pill}px`, backgroundColor: hubColors.chipText, opacity: 0.55 }} />
        <Box aria-hidden sx={{ position: 'absolute', top: 18, left: `${geo.marker}%`, transform: 'translateX(-50%)', width: 3, height: 18, borderRadius: `${layoutTokens.radius.pill}px`, backgroundColor: hubColors.interactivePrimary }} />
        <Box aria-hidden component="span" sx={{ position: 'absolute', top: 36, left: `${geo.bandLeft}%`, transform: 'translateX(-50%)', fontSize: 12.5, color: hubColors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
          {formatRent(asking.low)}
        </Box>
        <Box aria-hidden component="span" sx={{ position: 'absolute', top: 36, left: `${geo.bandLeft + geo.bandWidth}%`, transform: 'translateX(-50%)', fontSize: 12.5, color: hubColors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
          {formatRent(asking.high)}
        </Box>
      </Box>
      <Box component="p" sx={{ m: 0, fontSize: 13, lineHeight: 1.5, color: hubColors.textMuted }}>
        {tr('fair.source', { count: asking.count, km: asking.radiusKm })}
      </Box>

      <Box sx={{ mt: 6 }}>
        {vow.kind === 'loading' ? (
          <Box aria-busy aria-label={tr('fair.loading')} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: `${layoutTokens.radius.sm}px` }} />
            ))}
          </Box>
        ) : vow.kind === 'open' ? (
          <LeasedComparables leased={vow.leased} comparables={vow.comparables} />
        ) : vow.kind === 'error' ? (
          <Box component="p" role="status" sx={{ m: 0, fontSize: 14, color: hubColors.textSecondary }}>
            {tr('fair.error')}
          </Box>
        ) : (
          <GatedPanel
            title={tr('fair.gateTitle')}
            description={tr('fair.gateLine')}
            ctaLabel={signedIn ? tr('fair.gateCtaSignedIn') : tr('fair.gateCta')}
            rows={4}
            headingLevel="h3"
            trackAs={{ surface: 'page', listingKey: subjectKey, field: 'leased_comps' }}
          />
        )}
      </Box>
    </Box>
  );
}

function LeasedComparables({ leased, comparables }: { leased: RentBand | null; comparables: RentComparable[] }) {
  const tr = useRentalsText();
  if (!leased || comparables.length === 0) {
    return (
      <Box component="p" sx={{ m: 0, fontSize: 14, color: hubColors.textSecondary }}>
        {tr('fair.noComparables')}
      </Box>
    );
  }
  const spec = (c: RentComparable) =>
    [
      c.bedrooms != null ? tr('fair.specBeds', { count: c.bedrooms }) : null,
      c.bathrooms != null ? tr('fair.specBaths', { count: c.bathrooms }) : null,
      c.livingArea ? tr('fair.specSqft', { value: Math.round(c.livingArea).toLocaleString('en-CA') }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
  return (
    <Box data-leased-comparables>
      <Box component="h3" sx={{ m: 0, fontSize: 16, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
        {tr('fair.leasedTitle', { count: comparables.length, km: leased.radiusKm })}
      </Box>
      <Box component="p" sx={{ m: 0, mt: 1, fontSize: 14, color: hubColors.textSecondary }}>
        {tr('fair.leasedBand', { low: formatRent(leased.low), high: formatRent(leased.high) })}
      </Box>
      <Box component="table" sx={{ mt: 4, width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <Box component="thead">
          <Box component="tr" sx={{ color: hubColors.textMuted, textAlign: 'left' }}>
            <Box component="th" sx={{ py: 2, fontWeight: fontWeightTokens.medium }} />
            <Box component="th" sx={{ py: 2, fontWeight: fontWeightTokens.medium, textAlign: 'right' }}>{tr('fair.asked')}</Box>
            <Box component="th" sx={{ py: 2, pl: 3, fontWeight: fontWeightTokens.medium, textAlign: 'right' }}>{tr('fair.leased')}</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {comparables.map((c, i) => (
            <Box component="tr" key={`${c.address}-${i}`} sx={{ borderTop: hairline }}>
              <Box component="td" sx={{ py: 2.5, pr: 3 }}>
                <Box sx={{ color: hubColors.textPrimary, fontWeight: fontWeightTokens.medium }}>{c.address}</Box>
                <Box sx={{ fontSize: 13, color: hubColors.textMuted }}>{spec(c)}</Box>
              </Box>
              <Box component="td" sx={{ py: 2.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: hubColors.textSecondary }}>
                {c.listRent != null ? formatRent(c.listRent) : null}
              </Box>
              <Box component="td" sx={{ py: 2.5, pl: 3, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
                {c.leasedRent != null ? formatRent(c.leasedRent) : tr('fair.noLeasedRent')}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="p" sx={{ m: 0, mt: 3, fontSize: 12.5, lineHeight: 1.5, color: hubColors.textMuted }}>
        {tr('fair.vowNote')}
      </Box>
    </Box>
  );
}
